import { defineStore } from 'pinia';
import { EEnv } from '~/types/env';
import { Domains } from '~/utils/domains';
import { useStoreSsLocales } from '../softswiss/locales';
import { useStoreSsPlayer } from '../softswiss/player';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import type { TLanguage } from '~/types/language';
import type { IUrl } from '~/types/urls';

type SpecialDomainMode = 'promo' | null;
export type Geo = 'au' | 'main' | 'it' | 'et' | 'de-ch' | 'fi';
export type TAppType = 'fb' | 'main' | 'fb-inhouse';

export const useStoreRoot = defineStore('abstractRoot', {
  state: () => ({
    version: 'next',
    geo: '' as Geo,
    lang: '' as TLanguage,
    appType: 'main' as TAppType,
    currency: '' as string,
    env: EEnv.prod as EEnv, // default is stage for pages generation for feature
    specialMode: null as SpecialDomainMode,
    isUrlsFrozen: false as boolean,
    isEstonianLicense: false,
    defaultLocale: 'en',

    urls: {
      main: '',
      ss: '',
      // geoip: '',
      // blockchain: '',
    } as IUrl,

    domainSettings: {
      default: '7bitcasino.com' as string,
      main: [
        'win7bitcasino\\.com',
        'win7bitcasino1\\.com',
        '7bitcasino(8|9|10|11|12|13|14|18|19|44|88|28|99|100|101|111)?\\.com',
      ] as string[],
      urls: {
        ssApi: '/api' as string,
      },
      subDomains: {
        dev: ['local', '.+\\.stage'] as string[],
        stage: [
          'stage',
          'dev',
          'rc',
          'nuxt',
          'promo-stage',
          'promotion-stage',
          'promo-dev',
          'local-stage',
        ] as string[],
        prod: ['production-nuxt', 'prod-nuxt', 'classic', 'next'] as string[],
        special: {
          ss: 'www' as string,
          geoip: 'geoip' as string,
          blockchain: 'blockchain' as string,
          promo: ['promo', 'promotion', 'promo-stage', 'promotion-stage', 'promo-dev'] as string[],
        },
      },
    },

    screen: {
      width: NaN,
      height: NaN,
    },
  }),

  getters: {
    isPortraitOrientation(): boolean {
      return this.screen.width > this.screen.height;
    },

    /**
     * Get default language for app
     */
    defaultLang(): TLanguage {
      if (this.geo === 'it') {
        return 'it';
      }

      if (this.geo === 'et') {
        return 'et';
      }

      if (this.geo === 'de-ch') {
        return 'de';
      }

      return 'en';
    },

    /**
     * Getter for user lang
     * @param state Store state
     */
    userLang(state): TLanguage {
      return state.lang || this.defaultLang;
    },

    /**
     * Getter for user domain language.
     * Example:
     *  userLang = 'pl' => 'pl'
     *  userLang = 'en' => ''
     * @param getters Store getters
     */
    domainLang(): string {
      return this.userLang !== this.defaultLang ? this.userLang : '';
    },

    /**
     * Getter for default currency for app
     */
    defaultCurrency(): string {
      return useStoreCurrencyMenu().defaultCurrencyForCountry?.currencyCode || 'BTC';
    },

    /**
     * Getter for user currency
     * @param state Store state
     */
    userCurrency(state): string {
      const storeSsPlayer = useStoreSsPlayer();

      return state.currency || storeSsPlayer.playerCurrency || this.defaultCurrency;
    },

    /**
     * Getter for promo page mode detection
     * @param state Store state
     */
    isPromo(state): boolean {
      return state.specialMode === 'promo';
    },

    /**
     * main url of app
     * @param state Store state
     */
    mainUrl(state): string {
      return state.urls.main;
    },

    /**
     * softswis url for app
     * @param state Store state
     */
    ssUrl(state): string {
      return state.urls.ss;
    },

    /**
     * softswiss API url for app
     * @param state Store state
     */
    ssApiUrl(state): string {
      return state.urls.ss + state.domainSettings.urls.ssApi;
    },

    /**
     * update data interval for all rest requests (cache time)
     */
    updateInterval(): number {
      // one hour in miliseconds
      return 60 * 60 * 1000;
    },

    isDifferentLocaleDefault(state) {
      return state.defaultLocale !== 'en';
    },
    isFbTypeApp(state) {
      // return state.appType === 'main';
      return state.appType === 'fb' || state.appType === 'fb-inhouse';
    },
    isFbInhouseTypeApp(state) {
      return state.appType === 'fb-inhouse';
    },
  },

  actions: {
    /**
     * Set/update user's currency value
     *
     * @param currency Currency value
     */
    SET_USER_CURRENCY(currency: string) {
      this.currency = currency;
    },

    /**
     * Action for set enviropment
     * @param env enviropment type
     */
    setUrls(env?: EEnv) {
      const ds = this.domainSettings;

      const domains = new Domains({
        domains: ds.main,
        devSubDomains: ds.subDomains.dev,
        stagingSubDomains: ds.subDomains.stage,
        prodSubDomains: ds.subDomains.prod,
        specialSubDomains: {
          softswiss: ds.subDomains.special.ss,
          geoip: ds.subDomains.special.geoip,
          blockchain: ds.subDomains.special.blockchain,
          promo: ds.subDomains.special.promo,
        },
        specialUrls: { softswiss: ds.urls.ssApi },
        defaultDomain: ds.default,
        protocol: 'https',
      });

      if (!this.isUrlsFrozen) {
        this.urls = {
          main: domains.main,
          ss: domains.ssHost,
          // geoip: domains.geoip,
          // blockchain: domains.blockchain,
        };
      }

      if (domains.isPromoDomain) {
        this.specialMode = 'promo';
      } else {
        this.specialMode = null;
      }

      if (env) {
        this.env = env;
      } else if (domains.isDevDomain) {
        this.env = EEnv.dev;
      } else {
        this.env = EEnv.prod;
      }

      console.info(`APP MODE is [ ${this.env} ]`);
    },

    /**
     * Action for run currency mutation
     * @param currency Currency value
     */
    async setUserCurrency(currency: string) {
      await useStoreSsPlayer().setPlayerCurrency(currency);

      if (this.userCurrency !== currency && currency.length >= 3) {
        this.SET_USER_CURRENCY(currency);
      }
    },

    /**
     * Action for run lang mutation
     * @param lang Lang value
     */
    setUserLang(lang: TLanguage) {
      if (lang && this.userLang !== lang) {
        this.lang = lang;

        useStoreSsLocales().updateLocale(lang);
      }
    },

    setGeo(geo: Geo) {
      if (!import.meta.browser) {
        this.geo = geo;
        if (this.geo === 'et' || this.geo === 'fi') {
          this.isEstonianLicense = true;
        }
      }
    },

    setAppType(appType: TAppType) {
      this.appType = appType;
    },

    setDefaultLocale(value: string) {
      this.defaultLocale = value;
    },

    localePath(payload: string) {
      if (this.isDifferentLocaleDefault) {
        if (this.lang === 'en') {
          return `/en${useLocalePath()(payload)}`;
        }
        if (this.lang === this.defaultLocale) {
          return useLocalePath()(payload, 'en').replace(`/en`, '') || '/';
        }
      }
      return useLocalePath()(payload);
    },

    switchLocalePath(payload: string) {
      if (this.isDifferentLocaleDefault) {
        if (payload === this.defaultLocale) {
          return useSwitchLocalePath()(payload).replace(`/${this.defaultLocale}`, '') || '/';
        }
      }

      return useSwitchLocalePath()(payload);
    },

    setLang(payload: TLanguage) {
      this.lang = payload;
    },

    serverInit() {
      this.setUrls(EEnv.stage);
    },

    setScreen(payload: { height: number; width: number }) {
      this.screen = payload;
    },
  },
});
